import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../../Ui_Components/Button";

export type ReasonModalMode = "archive" | "restore";

interface IngredientReasonModalProps {
  open: boolean;
  mode: ReasonModalMode;
  ingredientName?: string;
  /* Noun used in the modal's title, question, and validation copy. Kept
     generic so the same modal can prompt for dish/recipe archive too;
     defaults to "ingredient" since that's the original caller. */
  itemLabel?: string;
  onClose: () => void;
  /* Called with the trimmed reason when the user confirms. Parent handles
     the backend call + toast messaging. */
  onConfirm: (reason: string) => void | Promise<void>;
}

type ModeCopy = {
  title: string;
  question: string;
  validationError: string;
  submittingLabel: string;
  confirmVariant: "primary" | "danger";
};

// Archive is a destructive action → danger variant. Restore brings a record
// back → the app's primary teal.
const buildCopy = (mode: ReasonModalMode, label: string): ModeCopy => {
  if (mode === "restore") {
    return {
      title: `Restore ${label}`,
      question: `What's reason to restore this ${label}?`,
      validationError: `Please provide a reason to restore this ${label}.`,
      submittingLabel: "Restoring...",
      confirmVariant: "primary",
    };
  }
  return {
    title: `Archive ${label}`,
    question: `What's reason to archive this ${label}?`,
    validationError: `Please provide a reason to archive this ${label}.`,
    submittingLabel: "Archiving...",
    confirmVariant: "danger",
  };
};

const IngredientReasonModal: React.FC<IngredientReasonModalProps> = ({
  open,
  mode,
  itemLabel = "ingredient",
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const copy = buildCopy(mode, itemLabel);
  const hasReason = reason.trim().length > 0;

  // Reset every time the dialog is (re)opened so a previous reason doesn't
  // linger on the next visit.
  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError(copy.validationError);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-md shadow-xl w-full max-w-[600px] mx-4">
        {/* Header */}
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-lg sm:text-xl 3xl:text-[22px] font-semibold font-inter text-[#1F1F1F]">
            {copy.title}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 pb-5">
          <label className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
            {copy.question}
          </label>

          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Write the reason"
            rows={5}
            disabled={submitting}
            className={`mt-2 w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter text-[#1F1F1F] placeholder-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#0DB5B5] resize-none disabled:opacity-60 ${
              error ? "border-[#F87171]" : "border-[#E0E0E0]"
            }`}
          />

          {error && (
            <p className="mt-2 text-xs 3xl:text-sm text-red-500 font-inter">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#E8E8E8] px-6 py-4 flex justify-end gap-3">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant={copy.confirmVariant}
            size="md"
            onClick={handleConfirm}
            loading={submitting}
            disabled={!hasReason || submitting}
          >
            {submitting ? copy.submittingLabel : "Yes"}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default IngredientReasonModal;
